/**
 * Area `personal-security` of the console gateway — over `apps/api/src/routes/personal-security/` (design §27.9.2).
 * Owned by plan-014 unit **C1**.
 *
 *   readMyProfile              `readMyProfile`               display name, email, locale, time zone
 *   readMySignInMethods        `listMySignInMethods`         ways to sign in + the second step
 *   readMyDevices              `listMyAuthSessions`          where the person is signed in
 *   changeMyPassword           `changeMyPassword`            signs out other devices
 *   unlinkMySignInMethod       `unlinkMySignInMethod`        never the last one (409 last_sign_in_method)
 *   startTotpEnrolment         `startMyTotpEnrolment`        the otpauth URI, once
 *   confirmTotpEnrolment       `confirmMyTotpEnrolment`      the first recovery codes, once
 *   regenerateRecoveryCodes    `regenerateMyRecoveryCodes`   new codes, once
 *   answerMfaChallenge         `answerMyMfaChallenge`        TOTP or a recovery code (also a fresh confirmation)
 *   revokeMyDevice             `revokeMyAuthSession`
 *   signOutMyOtherDevices      `signOutMyOtherAuthSessions`
 *   readSignInCodeAvailability `readSignInCodeAvailability`  can the log-in page offer an emailed code?
 *   requestSignInCode          `requestSignInCode`           the challenge cookie is set by the API (HttpOnly)
 *   verifySignInCode           `verifySignInCode`            an auth_session, adopted like log-in's
 *   requestInvitationSignInCode `requestSignInCode`          2026-11-03: `{ invitation_token }` — the code goes to the
 *                                                            invitation's own address (the join page)
 *   stepUpWithEmailCode        `stepUpAuthSession`           2026-11-03: `factor: "email_code"` — My security's
 *                                                            confirmation for a person whose only way in is the mailbox
 *   joinWaitlist               `joinWaitlist`                the way-in header's "Join the waitlist" (2026-10-15)
 *
 * ADDED 2026-10-25 (plan-028, design §39.2, §39.8; ADR 0080) — Google and Microsoft sign-in:
 *   readSignInProviders        `getSignInProviders`          which of the two buttons work
 *   beginProviderSignIn        `beginGoogleSignIn` / `beginMicrosoftSignIn`   the join page's start (token in the body)
 *   confirmProviderSignIn      `confirmProviderSignIn`       the code a held sign-in was mailed
 *   resendProviderSignInCode   `resendProviderSignInCode`    a new code for it
 *   linkMySignInMethod         `linkMySignInMethod`          My security's "Link" (a fresh confirmation first)
 *
 * Every security change answers `ApiError` `step_up_required` (401) without a fresh confirmation; the screen then
 * confirms (the password through `stepUpAuthSession`, or the authenticator through `answerMfaChallenge`) and
 * retries once.
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The eighteen operations are in C1's fragments; the merged spec and `@wetalk/api-client`'s generated client are
 * regenerated by the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through
 * {@link operationsOf}, a structural view of the client with exactly these methods: it compiles before and after
 * the regeneration, and once the client carries them the view is the client's own methods. A client that still
 * lacks one raises naming the regeneration, never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { AuthSessionView, ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readTextArray, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface PersonalProfileView {
  readonly displayName: string;
  readonly email: string;
  readonly emailVerified: boolean;
  readonly locale: string | null;
  readonly timeZone: string | null;
  readonly pendingEmailChange: { readonly email: string; readonly expiresAt: string } | null;
}

export interface ExternalSignInMethodView {
  readonly linked: boolean;
  readonly email: string | null;
  readonly linkedAt: string | null;
  /** Whether it can be linked from My security now (its sign-in is switched on and it is not linked yet). */
  readonly linkable: boolean;
}

export interface SignInMethodsView {
  readonly password: { readonly enabled: boolean; readonly changedAt: string | null };
  readonly google: ExternalSignInMethodView;
  readonly microsoft: ExternalSignInMethodView;
  readonly totp: { readonly state: "active" | "none"; readonly addedAt: string | null; readonly enrolling: boolean };
  readonly recoveryCodeRemaining: number;
  readonly recoveryCodeTotal: number;
  readonly secondStepEnforced: boolean;
  readonly secondStepVerified: boolean;
}

/** Who a sign-in was issued to: a browser's cookie, or the wetalk app on a phone (plan-034, migration 0206). */
export type DeviceClientKind = "browser" | "wetalk_app";
/** The phone a `wetalk_app` sign-in was issued on. */
export type DevicePlatform = "ios" | "android";

/**
 * One row of "Where you are signed in". A `wetalk_app` row names its phone in `devicePlatform` and never carries a
 * `userAgentLabel` (the screen builds its words from the two facts, plan-034 WAPP-DEVICE); a browser row has no
 * platform. Neither carries an absolute expiry: an app sign-in ends after 90 days without use (design §45.26).
 */
export type PersonDeviceView = PersonDeviceCommon &
  (
    | { readonly clientKind: "browser"; readonly devicePlatform: null }
    | { readonly clientKind: "wetalk_app"; readonly devicePlatform: DevicePlatform }
  );

interface PersonDeviceCommon {
  readonly authSessionId: string;
  readonly userAgentLabel: string | null;
  readonly approximatePlace: string | null;
  readonly createdAt: string;
  readonly lastSeenAt: string;
  readonly current: boolean;
  readonly operatorActing: boolean;
}

export type RemovableSignInMethod = "password" | "google" | "microsoft";
/** The two provider buttons (plan-028). */
export type SignInProviderName = "google" | "microsoft";
export type SecondStepFactor = "totp" | "recovery_code";

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The signed-in person's own profile — the same in every organization. */
    readMyProfile(): Promise<PersonalProfileView>;
    /** Saves the signed-in person's interface language through the existing profile route. */
    updateMyLocale(locale: "en" | "el"): Promise<PersonalProfileView>;
    /** Ways to sign in and the second step. */
    readMySignInMethods(): Promise<SignInMethodsView>;
    /** Where the person is signed in. */
    readMyDevices(): Promise<readonly PersonDeviceView[]>;
    /** Changes (or sets) the password; every other device is signed out. Returns how many. */
    changeMyPassword(newPassword: string): Promise<number>;
    /** Removes a way to sign in. Raises `last_sign_in_method` (409) for the last one. */
    unlinkMySignInMethod(method: RemovableSignInMethod): Promise<void>;
    /** Starts (or replaces) the authenticator app: the otpauth URI to render as a QR code, once. */
    startTotpEnrolment(): Promise<{ readonly otpauthUri: string; readonly expiresAt: string }>;
    /** Confirms the authenticator; the first one answers its recovery codes once. */
    confirmTotpEnrolment(code: string): Promise<{ readonly replaced: boolean; readonly recoveryCode: readonly string[] | null }>;
    /** New recovery codes; the old ones stop working. */
    regenerateRecoveryCodes(): Promise<readonly string[]>;
    /** Proves the second step (and confirms it is the person). Returns the recovery codes left. */
    answerMfaChallenge(factor: SecondStepFactor, code: string): Promise<number>;
    /** Signs out one of the person's other devices. */
    revokeMyDevice(authSessionId: string): Promise<void>;
    /** Signs out everywhere else. Returns how many devices. */
    signOutMyOtherDevices(): Promise<number>;
    /** Whether the log-in page may offer an emailed code (an email sender exists). */
    readSignInCodeAvailability(): Promise<boolean>;
    /** Emails a sign-in code (the same answer for every address); the API sets the challenge cookie. */
    requestSignInCode(emailAddress: string): Promise<{ readonly expiresAt: string }>;
    /** Signs in with the emailed code, in the browser that asked for it. */
    verifySignInCode(code: string): Promise<AuthSessionView>;
    /**
     * 2026-11-03 (design §16.37 amendment): emails a code to an open invitation's OWN address — the join page's "Email me
     * a code". A person who signs in already gets their ordinary sign-in code (then {@link verifySignInCode}); a new
     * address gets one it can only join with (`acceptInvitation` with `code`). The API sets the challenge cookie.
     */
    requestInvitationSignInCode(invitationToken: string): Promise<{ readonly expiresAt: string }>;
    /**
     * 2026-11-03: "Confirm it's you" with the code {@link requestSignInCode} mailed to the person — only for someone whose
     * one way in is their mailbox (no password, authenticator, Google or Microsoft). Raises `credential_invalid` (401)
     * for a wrong code, `step_up_unavailable` (403) for anyone else.
     */
    stepUpWithEmailCode(code: string): Promise<void>;
    /**
     * "Join the waitlist" from the way-in pages' header (Claude Design's `hwl` dialog, design audit 2026-10-15): the
     * public route `POST /v1/public/waitlist-signups`, stored before it answers `202`. Raises `ApiError` otherwise —
     * `409` when the form's words (`consentTextRevision`) are not the ones the API holds.
     */
    joinWaitlist(signup: WaitlistSignupRequest): Promise<void>;
    /** plan-028: which of "Continue with Google" and "Continue with Microsoft" work. */
    readSignInProviders(): Promise<Readonly<Record<SignInProviderName, boolean>>>;
    /** plan-028: the join page's start — the invitation token in the body, never a URL. The browser then goes to `startUrl`. */
    beginProviderSignIn(provider: SignInProviderName, start: { readonly returnPath: string; readonly invitationToken: string | null }): Promise<{ readonly startUrl: string }>;
    /** plan-028: the code a held Google or Microsoft sign-in was mailed. Signed in on success; returns where to go next. */
    confirmProviderSignIn(code: string): Promise<{ readonly returnPath: string }>;
    /** plan-028: a new code for the held sign-in. */
    resendProviderSignInCode(): Promise<void>;
    /** plan-028: links Google or Microsoft from My security; the browser then goes to `startUrl`. */
    linkMySignInMethod(provider: SignInProviderName): Promise<{ readonly startUrl: string }>;
    /**
     * ADR 0080 amendment 2026-10-25: "Continue with Google/Microsoft to confirm it's you", for a person with no password
     * and no authenticator. The browser then goes to `startUrl` and comes back to `returnPath` with `?step_up=`.
     */
    beginProviderStepUp(provider: SignInProviderName, returnPath: string): Promise<{ readonly startUrl: string }>;
  }
}

/** One "Join the waitlist" (the public site's `site/waitlist.ts` sends the same body). */
export interface WaitlistSignupRequest {
  readonly email: string;
  readonly solutionCode: string;
  readonly countryCode: "GR" | "CY" | "GB" | "IE" | "other";
  readonly callerLanguage: "en" | "el" | "other";
  readonly source: "nav_desktop" | "nav_mobile";
  readonly pageLanguage: "en" | "el";
  readonly consentTextRevision: string;
}

export type PersonalSecurityGatewaySlice = Pick<
  ConsoleGateway,
  | "readMyProfile"
  | "updateMyLocale"
  | "readMySignInMethods"
  | "readMyDevices"
  | "changeMyPassword"
  | "unlinkMySignInMethod"
  | "startTotpEnrolment"
  | "confirmTotpEnrolment"
  | "regenerateRecoveryCodes"
  | "answerMfaChallenge"
  | "revokeMyDevice"
  | "signOutMyOtherDevices"
  | "readSignInCodeAvailability"
  | "requestSignInCode"
  | "verifySignInCode"
  | "requestInvitationSignInCode"
  | "stepUpWithEmailCode"
  | "joinWaitlist"
  | "readSignInProviders"
  | "beginProviderSignIn"
  | "confirmProviderSignIn"
  | "resendProviderSignInCode"
  | "linkMySignInMethod"
  | "beginProviderStepUp"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in C1's fragments. */
export const personalSecurityGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface PersonalSecurityOperations {
  readMyProfile(): Promise<ApiResult>;
  updateMyProfile(request: { body?: unknown }): Promise<ApiResult>;
  listMySignInMethods(): Promise<ApiResult>;
  listMyAuthSessions(): Promise<ApiResult>;
  changeMyPassword(request: { body?: unknown }): Promise<ApiResult>;
  unlinkMySignInMethod(request: { path: { provider: string } }): Promise<ApiResult>;
  startMyTotpEnrolment(request: { body?: unknown }): Promise<ApiResult>;
  confirmMyTotpEnrolment(request: { body?: unknown }): Promise<ApiResult>;
  regenerateMyRecoveryCodes(request: { body?: unknown }): Promise<ApiResult>;
  answerMyMfaChallenge(request: { body?: unknown }): Promise<ApiResult>;
  revokeMyAuthSession(request: { path: { auth_session_id: string } }): Promise<ApiResult>;
  signOutMyOtherAuthSessions(request: { body?: unknown }): Promise<ApiResult>;
  readSignInCodeAvailability(): Promise<ApiResult>;
  requestSignInCode(request: { body?: unknown }): Promise<ApiResult>;
  verifySignInCode(request: { body?: unknown }): Promise<ApiResult>;
  stepUpAuthSession(request: { body?: unknown }): Promise<ApiResult>;
  joinWaitlist(request: { body?: unknown }): Promise<ApiResult>;
  getSignInProviders(): Promise<ApiResult>;
  beginGoogleSignIn(request: { body?: unknown }): Promise<ApiResult>;
  beginMicrosoftSignIn(request: { body?: unknown }): Promise<ApiResult>;
  confirmProviderSignIn(request: { body?: unknown }): Promise<ApiResult>;
  resendProviderSignInCode(request: { body?: unknown }): Promise<ApiResult>;
  linkMySignInMethod(request: { path: { provider: string } }): Promise<ApiResult>;
  beginProviderStepUp(request: { body?: unknown }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): PersonalSecurityOperations {
  const held = client as unknown as Partial<Record<keyof PersonalSecurityOperations, unknown>>;
  return new Proxy({} as PersonalSecurityOperations, {
    get(_target, name: string) {
      const found = held[name as keyof PersonalSecurityOperations];
      if (typeof found !== "function") {
        throw new Error(
          `The generated API client has no "${name}". Regenerate it (pnpm api:build-spec, the api-client lock holder) ` +
            "so it carries C1's personal-security operations.",
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function externalOf(value: unknown, path: string): ExternalSignInMethodView {
  const one = readRecord(value, path);
  return {
    linked: readBoolean(one["linked"], `${path}.linked`),
    email: textOrNull(one["email"], `${path}.email`),
    linkedAt: textOrNull(one["linked_at"], `${path}.linked_at`),
    linkable: readBoolean(one["linkable"], `${path}.linkable`),
  };
}

export function profileOfWire(data: Readonly<Record<string, unknown>>): PersonalProfileView {
  const pending = data["pending_email_change"];
  return {
    displayName: readText(data["display_name"], "readMyProfile.display_name"),
    email: readText(data["email"], "readMyProfile.email"),
    emailVerified: readBoolean(data["email_verified"], "readMyProfile.email_verified"),
    locale: textOrNull(data["locale"], "readMyProfile.locale"),
    timeZone: textOrNull(data["time_zone"], "readMyProfile.time_zone"),
    pendingEmailChange:
      pending === null
        ? null
        : (() => {
            const one = readRecord(pending, "readMyProfile.pending_email_change");
            return { email: readText(one["email"], "pending.email"), expiresAt: readText(one["expires_at"], "pending.expires_at") };
          })(),
  };
}

export function signInMethodsOfWire(data: Readonly<Record<string, unknown>>): SignInMethodsView {
  const password = readRecord(data["password"], "listMySignInMethods.password");
  const totp = readRecord(data["totp"], "listMySignInMethods.totp");
  const state = readText(totp["state"], "listMySignInMethods.totp.state");
  if (state !== "active" && state !== "none") throw new Error(`listMySignInMethods: "${state}" is not a totp state`);
  return {
    password: {
      enabled: readBoolean(password["enabled"], "password.enabled"),
      changedAt: textOrNull(password["changed_at"], "password.changed_at"),
    },
    google: externalOf(data["google"], "listMySignInMethods.google"),
    microsoft: externalOf(data["microsoft"], "listMySignInMethods.microsoft"),
    totp: {
      state,
      addedAt: textOrNull(totp["added_at"], "totp.added_at"),
      enrolling: readBoolean(totp["enrolling"], "totp.enrolling"),
    },
    recoveryCodeRemaining: readWholeNumber(data["recovery_code_remaining"], "recovery_code_remaining"),
    recoveryCodeTotal: readWholeNumber(data["recovery_code_total"], "recovery_code_total"),
    secondStepEnforced: readBoolean(data["second_step_enforced"], "second_step_enforced"),
    secondStepVerified: readBoolean(data["second_step_verified"], "second_step_verified"),
  };
}

/**
 * The row's client: `client_kind` and `device_platform` (plan-034 API-DEVICE). An app row without a platform, a
 * browser row with one, or an unknown word raises — the database CHECKs the same shape (`auth_session_client_shape`).
 *
 * TEMPORARY TOLERANCE (plan-034 WAPP-DEVICE; deleted at INT once the api is live): an answer from an api that predates
 * plan-034 carries neither field, and every row it lists is a browser's, so the portal may go out with or after the
 * api. Only both fields absent together is read that way; one without the other still raises.
 */
function clientOfWire(
  one: Readonly<Record<string, unknown>>,
  path: string,
): { readonly clientKind: "browser"; readonly devicePlatform: null } | { readonly clientKind: "wetalk_app"; readonly devicePlatform: DevicePlatform } {
  if (!("client_kind" in one) && !("device_platform" in one)) return { clientKind: "browser", devicePlatform: null };
  const kind = readText(one["client_kind"], `${path}.client_kind`);
  const platform = one["device_platform"];
  if (kind === "browser") {
    if (platform !== null) throw new Error(`${path}.device_platform: a browser sign-in names no platform`);
    return { clientKind: "browser", devicePlatform: null };
  }
  if (kind === "wetalk_app") {
    const named = readText(platform, `${path}.device_platform`);
    if (named !== "ios" && named !== "android") throw new Error(`${path}.device_platform: "${named}" is not a device platform`);
    return { clientKind: "wetalk_app", devicePlatform: named };
  }
  throw new Error(`${path}.client_kind: "${kind}" is not a client kind`);
}

export function deviceOfWire(value: unknown, index: number): PersonDeviceView {
  const path = `listMyAuthSessions.data[${String(index)}]`;
  const one = readRecord(value, path);
  return {
    ...clientOfWire(one, path),
    authSessionId: readText(one["auth_session_id"], `${path}.auth_session_id`),
    userAgentLabel: textOrNull(one["user_agent_label"], `${path}.user_agent_label`),
    approximatePlace: textOrNull(one["approximate_place"], `${path}.approximate_place`),
    createdAt: readText(one["created_at"], `${path}.created_at`),
    lastSeenAt: readText(one["last_seen_at"], `${path}.last_seen_at`),
    current: readBoolean(one["current"], `${path}.current`),
    operatorActing: readBoolean(one["operator_acting"], `${path}.operator_acting`),
  };
}

export function personalSecurityApiSlice(client: WeTalkApiClient, context: SliceContext): PersonalSecurityGatewaySlice {
  const api = (): PersonalSecurityOperations => operationsOf(client);
  return {
    async readMyProfile() {
      return profileOfWire(dataOf(await api().readMyProfile(), "readMyProfile"));
    },
    async updateMyLocale(locale) {
      return profileOfWire(dataOf(await api().updateMyProfile({ body: { locale } }), "updateMyProfile"));
    },
    async readMySignInMethods() {
      return signInMethodsOfWire(dataOf(await api().listMySignInMethods(), "listMySignInMethods"));
    },
    async readMyDevices() {
      const body = readRecord(assertOk(await api().listMyAuthSessions()), "listMyAuthSessions");
      return readArray(body["data"], "listMyAuthSessions.data").map(deviceOfWire);
    },
    async changeMyPassword(newPassword) {
      const data = dataOf(await api().changeMyPassword({ body: { new_password: newPassword } }), "changeMyPassword");
      return readWholeNumber(data["signed_out_device"], "changeMyPassword.signed_out_device");
    },
    async unlinkMySignInMethod(method) {
      assertOk(await api().unlinkMySignInMethod({ path: { provider: method } }));
    },
    async startTotpEnrolment() {
      const data = dataOf(await api().startMyTotpEnrolment({}), "startMyTotpEnrolment");
      return {
        otpauthUri: readText(data["otpauth_uri"], "startMyTotpEnrolment.otpauth_uri"),
        expiresAt: readText(data["expires_at"], "startMyTotpEnrolment.expires_at"),
      };
    },
    async confirmTotpEnrolment(code) {
      const data = dataOf(await api().confirmMyTotpEnrolment({ body: { code } }), "confirmMyTotpEnrolment");
      const codes = data["recovery_code"];
      return {
        replaced: readBoolean(data["replaced"], "confirmMyTotpEnrolment.replaced"),
        recoveryCode: codes === null ? null : readTextArray(codes, "confirmMyTotpEnrolment.recovery_code"),
      };
    },
    async regenerateRecoveryCodes() {
      const data = dataOf(await api().regenerateMyRecoveryCodes({}), "regenerateMyRecoveryCodes");
      return readTextArray(data["recovery_code"], "regenerateMyRecoveryCodes.recovery_code");
    },
    async answerMfaChallenge(factor, code) {
      const data = dataOf(await api().answerMyMfaChallenge({ body: { factor, code } }), "answerMyMfaChallenge");
      return readWholeNumber(data["recovery_code_remaining"], "answerMyMfaChallenge.recovery_code_remaining");
    },
    async revokeMyDevice(authSessionId) {
      assertOk(await api().revokeMyAuthSession({ path: { auth_session_id: authSessionId } }));
    },
    async signOutMyOtherDevices() {
      const data = dataOf(await api().signOutMyOtherAuthSessions({}), "signOutMyOtherAuthSessions");
      return readWholeNumber(data["signed_out_device"], "signOutMyOtherAuthSessions.signed_out_device");
    },
    async readSignInCodeAvailability() {
      return readBoolean(dataOf(await api().readSignInCodeAvailability(), "readSignInCodeAvailability")["available"], "available");
    },
    async requestSignInCode(emailAddress) {
      const data = dataOf(await api().requestSignInCode({ body: { email: emailAddress } }), "requestSignInCode");
      return { expiresAt: readText(data["expires_at"], "requestSignInCode.expires_at") };
    },
    async verifySignInCode(code) {
      return context.adoptAuthSession(assertOk(await api().verifySignInCode({ body: { code } })), "verifySignInCode");
    },
    async requestInvitationSignInCode(invitationToken) {
      const data = dataOf(await api().requestSignInCode({ body: { invitation_token: invitationToken } }), "requestSignInCode");
      return { expiresAt: readText(data["expires_at"], "requestSignInCode.expires_at") };
    },
    async stepUpWithEmailCode(code) {
      assertOk(await api().stepUpAuthSession({ body: { factor: "email_code", code } }));
    },
    async readSignInProviders() {
      const data = dataOf(await api().getSignInProviders(), "getSignInProviders");
      return { google: readBoolean(data["google"], "getSignInProviders.google"), microsoft: readBoolean(data["microsoft"], "getSignInProviders.microsoft") };
    },
    async beginProviderSignIn(provider, start) {
      const body = { return_path: start.returnPath, ...(start.invitationToken === null ? {} : { invitation_token: start.invitationToken }) };
      const result = provider === "google" ? await api().beginGoogleSignIn({ body }) : await api().beginMicrosoftSignIn({ body });
      const operationId = provider === "google" ? "beginGoogleSignIn" : "beginMicrosoftSignIn";
      return { startUrl: readText(dataOf(result, operationId)["start_url"], `${operationId}.start_url`) };
    },
    async confirmProviderSignIn(code) {
      const data = dataOf(await api().confirmProviderSignIn({ body: { code } }), "confirmProviderSignIn");
      return { returnPath: readText(data["return_path"], "confirmProviderSignIn.return_path") };
    },
    async resendProviderSignInCode() {
      assertOk(await api().resendProviderSignInCode({}));
    },
    async linkMySignInMethod(provider) {
      const data = dataOf(await api().linkMySignInMethod({ path: { provider } }), "linkMySignInMethod");
      return { startUrl: readText(data["start_url"], "linkMySignInMethod.start_url") };
    },
    async beginProviderStepUp(provider, returnPath) {
      const data = dataOf(await api().beginProviderStepUp({ body: { provider, return_path: returnPath } }), "beginProviderStepUp");
      return { startUrl: readText(data["start_url"], "beginProviderStepUp.start_url") };
    },
    async joinWaitlist(signup) {
      assertOk(
        await api().joinWaitlist({
          body: {
            email: signup.email.trim(),
            solution_code: signup.solutionCode,
            country_code: signup.countryCode,
            caller_language: signup.callerLanguage,
            source: signup.source,
            page_language: signup.pageLanguage,
            consent_text_revision: signup.consentTextRevision,
          },
        }),
      );
    },
  };
}
